// src/domains/dash.ts —— dash 原生页分派器（dashboard / upstream / roles / tgbot / broadcast 五个页子模块；tgbot 只承载页签 1「当前 Bot 配置」，C-15；
// broadcast 为 0049 Bot 广告推送整页，C-33）
// 分派规则（契约文件）：
//   vals          恒合并五个 part 的键（键集两两互斥、并集 = dash.keys.ts 全集）；只有 enter 按页门控
//   session:lost  五 part reset（换代清空缓存与在途）
//   session:ready 五 part reset，再对当前页所属 part 调 enter（落地页 dashboard 不经切页，只能由此加载）
//   切页          update 中 prev.page !== page 且会话就绪 → 新页所属 part 调 enter
//   同页重复点击  外壳发 screen{from===to}（E-10）→ 当前页所属 part reset + enter
import { dashDefaults } from '../state';
import { I18N_BROADCAST } from '../i18n/broadcast';
import { DASH_BROADCAST } from './dash.broadcast';
import { KEYS } from './dash.keys';
import { DASH_DASHBOARD } from './dash.dashboard';
import { DASH_ROLES } from './dash.roles';
import { DASH_TGBOT } from './dash.tgbot';
import { DASH_UPSTREAM } from './dash.upstream';
import type { AdminCtx, AdminDomain } from './types';

// 页子模块统一形状。enter 由分派器在事件回调中调用：读状态用 ctx.s（分派器传入的 ctx.s 为实时 host.state）
export interface DashPart {
  vals(ctx: AdminCtx): Readonly<Record<string, unknown>>; // 该 part 负责的模板键
  enter(ctx: AdminCtx): void; // 对当前页触发加载
  reset(host: object): void; // 换代清空本 part 缓存与在途
}

const PARTS: readonly DashPart[] = [DASH_DASHBOARD, DASH_UPSTREAM, DASH_ROLES, DASH_TGBOT, DASH_BROADCAST];

function partOf(page: string): DashPart | null {
  if (page === 'dashboard') return DASH_DASHBOARD;
  if (page === 'upstream') return DASH_UPSTREAM;
  if (page === 'roles') return DASH_ROLES;
  if (page === 'tgbot') return DASH_TGBOT;
  if (page === 'broadcast') return DASH_BROADCAST;
  return null;
}

// ---- 会话就绪态（每 host 一份） ----

interface DsRun {
  ready: boolean;
}

const runs = new WeakMap<object, DsRun>();
const unsubs = new WeakMap<object, ReadonlyArray<() => void>>();

// 未经 mount 的宿主（域单测直接构造的 AdminCtx）视为已就绪；产品宿主由 mount 置为未就绪，等 session:ready
function isReady(host: object): boolean {
  return runs.get(host)?.ready ?? true;
}

// 事件回调持有的是挂载时构造的 ctx（其 s 为当时快照）：传给 part 前把 s 换成实时 host.state
function live(ctx: AdminCtx): AdminCtx {
  const host = ctx.host;
  return {
    host,
    get s() {
      return host.state;
    },
    t: ctx.t,
    lang: ctx.lang,
    gp: ctx.gp,
    events: ctx.events,
  };
}

function resetAll(host: object): void {
  for (const p of PARTS) p.reset(host);
}

export const dash: AdminDomain = {
  name: 'dash',
  keys: KEYS,
  legacyKeysFor: () => [],
  i18n: I18N_BROADCAST, // 0049 C-33 广告推送页与个人设置 Telegram ID 文案（bc* / myTg*）
  state: () => ({ ds: dashDefaults() }),
  mount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    runs.set(ctx.host, { ready: false });
    unsubs.set(ctx.host, [
      ctx.events.on('session:lost', () => {
        runs.set(ctx.host, { ready: false });
        resetAll(ctx.host);
      }),
      ctx.events.on('session:ready', () => {
        runs.set(ctx.host, { ready: true });
        resetAll(ctx.host);
        partOf(ctx.host.state.page)?.enter(live(ctx));
      }),
      ctx.events.on('screen', (p) => {
        if (p.from !== p.to || !isReady(ctx.host)) return;
        const part = partOf(ctx.host.state.page);
        if (!part) return;
        part.reset(ctx.host);
        part.enter(live(ctx));
      }),
    ]);
  },
  unmount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    unsubs.delete(ctx.host);
    runs.delete(ctx.host);
  },
  update(ctx, prev) {
    const page = ctx.host.state.page;
    if (prev.page === page || !isReady(ctx.host)) return;
    partOf(page)?.enter(ctx);
  },
  vals: (ctx) => ({ ...DASH_TGBOT.vals(ctx), ...DASH_DASHBOARD.vals(ctx), ...DASH_UPSTREAM.vals(ctx), ...DASH_ROLES.vals(ctx), ...DASH_BROADCAST.vals(ctx) }),
};
